import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import moment from "moment-timezone";
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ARGENTINA_TIMEZONE = 'America/Argentina/Buenos_Aires';
const HEALTH_RECORDS_URL = 'https://iron.nextriking.com/api/health-records';

const Members = () => {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editedValues, setEditedValues] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: 'lastname', direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [ordenMesPagadoAsc, setOrdenMesPagadoAsc] = useState(true);
    const [statusFilter, setStatusFilter] = useState('active');
    const [exportFilter, setExportFilter] = useState('active');
    const navigate = useNavigate();

    const parsePaymentDate = (dateValue) => {
        if (!dateValue) return null;

        const parsedDate = moment.utc(dateValue);
        return parsedDate.isValid() ? parsedDate : null;
    };

    const formatPaymentDate = (dateValue, format = 'DD/MM/YYYY') => {
        const parsedDate = parsePaymentDate(dateValue);
        return parsedDate ? parsedDate.format(format) : 'No registrada';
    };

    const getTodayInArgentina = () => moment().tz(ARGENTINA_TIMEZONE).format('YYYY-MM-DD');

    const getMembershipExpiration = (user) => user.membership_expires_at || user.membershipExpiresAt;



    useEffect(() => {
        fetchUsers()

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };

    }, []);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };


    const handleFilterChange = (e) => {
        setSelectedFilter(e.target.value);
    };

    const filteredUsers = Array.isArray(users) ? users.filter((user) => {
        if (statusFilter === 'active' && user.active === 0) return false;
        if (statusFilter === 'disabled' && user.active !== 0) return false;
        if (!searchTerm) return true;
        switch (selectedFilter) {
            case 'name':
                return user.name?.toLowerCase().includes(searchTerm.toLowerCase());
            case 'lastname':
                return user.lastname?.toLowerCase().includes(searchTerm.toLowerCase());
            case 'documentNumber':
                return String(user.documentNumber || "").includes(searchTerm.toLowerCase());
            default:
                return true;
        }
    }) : [];

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

    const paymentAtDay = (expirationDate) => {
        if (!expirationDate) return false;

        const vencimiento = parsePaymentDate(expirationDate);
        if (!vencimiento) return false;

        const hoy = moment().tz(ARGENTINA_TIMEZONE).startOf('day');

        return hoy.isBefore(vencimiento);
    };

    const formatPaymentMethod = (method) => ({
        cash: 'Efectivo',
        transfer: 'Transferencia',
        mercadopago: 'Mercado Pago',
        card: 'Tarjeta',
        other: 'Otro',
    }[method] || 'No registrado');

    const calculateMembershipExpiration = (paymentDate, currentExpiration, planMonths) => {
        const payment = moment.tz(paymentDate, 'YYYY-MM-DD', true, ARGENTINA_TIMEZONE);
        if (!payment.isValid()) return null;

        const current = parsePaymentDate(currentExpiration);
        const baseDate = current?.isAfter(payment, 'day') ? current : payment;
        return baseDate.clone().add(Number(planMonths), 'months').format('YYYY-MM-DD');
    };

    const isPaymentNearExpiration = (user) => {
        const expiration = parsePaymentDate(getMembershipExpiration(user));
        if (!expiration || user.active === 0) return false;

        const today = moment().tz(ARGENTINA_TIMEZONE).startOf('day');
        const daysUntilExpiration = expiration.startOf('day').diff(today, 'days');
        return daysUntilExpiration >= 1 && daysUntilExpiration <= 5;
    };

    const getPaymentReminderLink = (phone) => {
        const message = 'Te recordamos que tu cuota está próxima a vencer.';
        return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    };


    const payMonthorder = () => {
        const miembrosOrdenados = [...users].sort((a, b) => {
            const aAlDia = paymentAtDay(getMembershipExpiration(a));
            const bAlDia = paymentAtDay(getMembershipExpiration(b));

            if (aAlDia === bAlDia) return 0;
            return ordenMesPagadoAsc ? aAlDia - bAlDia : bAlDia - aAlDia;
        });

        setUsers(miembrosOrdenados);
        setOrdenMesPagadoAsc(!ordenMesPagadoAsc);
    };

    // ordenamiento INSENSITIVE
    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sortedUsers = [...users].sort((a, b) => {
            return direction === "asc"
                ? a[key].localeCompare(b[key], undefined, { sensitivity: "base" })
                : b[key].localeCompare(a[key], undefined, { sensitivity: "base" });
        });

        setUsers(sortedUsers);
    };
    const sortDates = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }

        setSortConfig({ key, direction });

        const sortedUsers = [...users].sort((a, b) => {
            const dateA = new Date(a[key]);
            const dateB = new Date(b[key]);

            return direction === "asc" ? dateA - dateB : dateB - dateA;
        });

        setUsers(sortedUsers);
    };

    const handleOnline = () => {
        Swal.fire({
            icon: 'success',
            title: 'Conexión restablecida',
            text: 'Ahora estás online.',
        });
    };

    const handleOffline = () => {
        Swal.fire({
            icon: 'error',
            title: 'Sin conexión',
            text: 'Parece que estás offline. Verifica tu conexión a Internet.',
        });
    };


    const handleEdit = (user) => {
        setEditingUserId(user.id);
        setEditedValues(user);
    };

    const handleCancelEdit = () => {
        setEditingUserId(null);
        setEditedValues({});
    };
    const handleSaveEdit = async () => {
        try {
            const formatDate = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);

                return date.toISOString().slice(0, 19).replace('T', ' ');
            };

            const formattedValues = {
                ...editedValues,
                birthDate: formatDate(editedValues.birthDate),
                firstPayment: formatDate(editedValues.firstPayment),
            };

            const response = await axios.post(
                `https://iron.nextriking.com/api/users/add`,
                formattedValues
            );

            if (response.status === 200) {
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        user.id === editingUserId ? { ...user, ...formattedValues } : user
                    )
                );

                setEditingUserId(null);
                setEditedValues({});

                await Swal.fire('¡Éxito!', 'Usuario actualizado correctamente.', 'success');
            }
        } catch (error) {
            Swal.fire(
                'Error',
                error.response ? error.response.data.error : 'Error al actualizar el usuario.',
                'error'
            );
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedValues((prev) => ({ ...prev, [name]: value }));
    };

    const fetchUsers = async () => {
        Swal.fire({
            title: 'Cargando usuarios...',
            text: 'Por favor espera unos momentos.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            const response = await axios.get('https://iron.nextriking.com/api/users');
            setUsers(response.data.body);
            Swal.close();
        } catch (error) {
            setError('Error al obtener los usuarios. Inténtalo de nuevo más tarde.');
            console.error('Error al obtener los usuarios:', error);
            Swal.fire('Error', 'No se pudieron cargar los usuarios. Inténtalo más tarde.', 'error');
        }
    };

    const handleDelete = async (userId) => {
        const confirmDelete = await Swal.fire({
            title: '¿Estás seguro?',
            text: "¡No podrás recuperar este usuario!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, elimínalo'
        });

        if (confirmDelete.isConfirmed) {
            try {
                const response = await axios.delete('https://iron.nextriking.com/api/users/delete', {
                    data: { id: userId }
                });

                if (response.status === 200) {
                    Swal.fire(
                        '¡Eliminado!',
                        response.data.message,
                        'success'
                    );
                    setUsers(users.filter(user => user.id !== userId));
                } else {
                    Swal.fire(
                        'Error',
                        response.data.error || 'Error al eliminar el usuario.',
                        'error'
                    );
                }
            } catch (error) {
                Swal.fire(
                    'Error',
                    error.response ? error.response.data.error : 'Error al eliminar el usuario.',
                    'error'
                );
            }
        }
    };
    const getServiceName = (serviceId) => {
        switch (serviceId) {
            case 1: return "Sala libre";
            case 2: return "Sala HIFT";
            case 3: return "Personalizado";
            default: return serviceId;
        }
    };

    const handleUpdatePayment = async (user) => {
        const currentExpiration = getMembershipExpiration(user);
        const { value: payment, isConfirmed } = await Swal.fire({
            title: "Registrar pago",
            html: `
                <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; margin-bottom: 12px; text-align: left;">
                    <label for="payment-date">Fecha de pago</label>
                    <input type="date" id="payment-date" class="swal2-input" style="box-sizing: border-box; margin: 0; width: 100%;" value="${getTodayInArgentina()}">
                </div>
                <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; margin-bottom: 12px; text-align: left;">
                    <label for="payment-plan">Duración</label>
                    <select id="payment-plan" class="swal2-select" style="box-sizing: border-box; margin: 0; width: 100%;">
                        <option value="1">1 mes</option>
                        <option value="3">3 meses</option>
                        <option value="6">6 meses</option>
                        <option value="12">1 año</option>
                    </select>
                </div>
                <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; text-align: left;">
                    <label for="payment-amount">Importe</label>
                    <input type="number" id="payment-amount" class="swal2-input" style="box-sizing: border-box; margin: 0; width: 100%;" min="0.01" step="0.01" placeholder="0,00">
                </div>
                <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; margin-top: 12px; text-align: left;">
                    <label for="payment-method">Medio de pago</label>
                    <select id="payment-method" class="swal2-select" style="box-sizing: border-box; margin: 0; width: 100%;">
                        <option value="cash">Efectivo</option>
                        <option value="transfer">Transferencia</option>
                        <option value="mercadopago">Mercado Pago</option>
                        <option value="card">Tarjeta</option>
                        <option value="other">Otro</option>
                    </select>
                </div>
                <p id="payment-expiration" style="margin: 12px 0 0;"></p>
                `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: "Registrar pago",
            cancelButtonText: "Cancelar",
            didOpen: () => {
                const dateInput = document.getElementById('payment-date');
                const planInput = document.getElementById('payment-plan');
                const expiration = document.getElementById('payment-expiration');
                const updateExpiration = () => {
                    const expiresAt = calculateMembershipExpiration(dateInput.value, currentExpiration, planInput.value);
                    expiration.textContent = expiresAt
                        ? `Vencimiento: ${formatPaymentDate(expiresAt)}`
                        : '';
                };

                dateInput.addEventListener('change', updateExpiration);
                planInput.addEventListener('change', updateExpiration);
                updateExpiration();
            },
            preConfirm: () => {
                const date = document.getElementById("payment-date").value;
                const planMonths = Number(document.getElementById('payment-plan').value);
                const amount = Number(document.getElementById('payment-amount').value);
                const paymentMethod = document.getElementById('payment-method').value;
                if (!date || ![1, 3, 6, 12].includes(planMonths) || !Number.isFinite(amount) || amount <= 0) {
                    Swal.showValidationMessage("Completá una fecha, un plan y un importe mayor a cero");
                    return false;
                }
                return { date, planMonths, amount, paymentMethod };
            }
        });

        if (!isConfirmed || !payment) return;

        try {
            const response = await fetch(`https://iron.nextriking.com/api/users/${user.id}/update-payment`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payment),
            });

            if (!response.ok) throw new Error("Error al actualizar");

            const data = await response.json();

            setUsers((currentUsers) => currentUsers.map((currentUser) =>
                currentUser.id === user.id
                    ? {
                        ...currentUser,
                        lastPayment: payment.date,
                        membership_expires_at: data.membershipExpiresAt,
                    }
                    : currentUser
            ));

            Swal.fire({
                title: "¡Actualizado!",
                text: data.message,
                icon: "success",
            });

        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Error",
                text: "Hubo un problema al actualizar la fecha de pago.",
                icon: "error",
            });
        }
    };
    const handleViewPaymentHistory = async (userId, userName) => {
        try {
            const response = await fetch(`https://iron.nextriking.com/api/users/${userId}/payment-history`);
            const data = await response.json();

            if (data.error || !data.body) {
                throw new Error('Error al obtener historial');
            }

            const historyHtml = data.body.length > 0
                ? data.body.map(payment => `
                <div style="text-align: left; padding: 8px; border-bottom: 1px solid #eee;">
                    <strong>${formatPaymentDate(payment.payment_date)}</strong>
                    ${payment.planMonths ? `<br><small>Plan: ${payment.planMonths === 12 ? '1 año' : `${payment.planMonths} ${payment.planMonths === 1 ? 'mes' : 'meses'}`}</small>` : '<br><small>Pago registrado sin plan</small>'}
                    ${payment.amount ? `<br><small>Importe: ${new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(payment.amount))}</small>` : ''}
                    <br><small>Medio: ${formatPaymentMethod(payment.paymentMethod)}</small>
                    ${payment.expiresAt ? `<br><small>Vence: ${formatPaymentDate(payment.expiresAt)}</small>` : ''}
                    ${payment.registered_by_name ? `<br><small>Registrado por: ${payment.registered_by_name} ${payment.registered_by_lastname}</small>` : ''}
                </div>
              `).join('')
                : '<p>No hay pagos registrados</p>';

            Swal.fire({
                title: `Historial de Pagos - ${userName}`,
                html: historyHtml,
                width: '500px',
                confirmButtonText: 'Cerrar'
            });

        } catch (error) {
            console.error(error);
            Swal.fire({
                title: "Error",
                text: "No se pudo cargar el historial de pagos",
                icon: "error"
            });
        }
    };

    const handleHealthRecord = async (user) => {
        try {
            const response = await axios.get(`${HEALTH_RECORDS_URL}/user/${user.id}`);
            const record = response.data.body || {};
            const result = await Swal.fire({
                title: `Ficha de salud: ${[user.name, user.lastname].filter(Boolean).join(' ')}`,
                html: `
                    <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; margin-bottom: 12px; text-align: left;">
                        <label for="health-medical-conditions">Condiciones médicas</label>
                        <textarea id="health-medical-conditions" class="swal2-textarea" style="box-sizing: border-box; margin: 0; width: 100%;" aria-label="Condiciones médicas"></textarea>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; margin-bottom: 12px; text-align: left;">
                        <label for="health-injuries-restrictions">Lesiones o restricciones</label>
                        <textarea id="health-injuries-restrictions" class="swal2-textarea" style="box-sizing: border-box; margin: 0; width: 100%;" aria-label="Lesiones o restricciones"></textarea>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; margin-bottom: 12px; text-align: left;">
                        <label for="health-allergies">Alergias</label>
                        <textarea id="health-allergies" class="swal2-textarea" style="box-sizing: border-box; margin: 0; width: 100%;" aria-label="Alergias"></textarea>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: stretch; gap: 4px; text-align: left;">
                        <label for="health-observations">Observaciones</label>
                        <textarea id="health-observations" class="swal2-textarea" style="box-sizing: border-box; margin: 0; width: 100%;" aria-label="Observaciones"></textarea>
                    </div>
                `,
                width: '650px',
                showCancelButton: true,
                confirmButtonText: 'Guardar ficha',
                cancelButtonText: 'Cancelar',
                didOpen: () => {
                    document.getElementById('health-medical-conditions').value = record.medicalConditions || '';
                    document.getElementById('health-injuries-restrictions').value = record.injuriesRestrictions || '';
                    document.getElementById('health-allergies').value = record.allergies || '';
                    document.getElementById('health-observations').value = record.observations || '';
                },
                preConfirm: () => ({
                    medicalConditions: document.getElementById('health-medical-conditions').value,
                    injuriesRestrictions: document.getElementById('health-injuries-restrictions').value,
                    allergies: document.getElementById('health-allergies').value,
                    observations: document.getElementById('health-observations').value,
                }),
            });

            if (!result.isConfirmed) return;

            await axios.post(HEALTH_RECORDS_URL, { userId: user.id, ...result.value });
            Swal.fire('Ficha guardada', 'La información de salud se actualizó correctamente.', 'success');
        } catch (error) {
            console.error('Error al guardar la ficha de salud:', error);
            Swal.fire('Error', 'No se pudo cargar o guardar la ficha de salud.', 'error');
        }
    };
    const totalUsers = Array.isArray(users)
        ? users.filter(user => user.usertype === 3 && user.active !== 0)
        : [];

    const paidUsersCount = totalUsers.filter(user => paymentAtDay(getMembershipExpiration(user))).length;

    const getMembersToExport = () => exportFilter === 'unpaid'
        ? totalUsers.filter((user) => !paymentAtDay(getMembershipExpiration(user)))
        : totalUsers;

    const getActiveMembersExportData = () => getMembersToExport().map((user) => ({
        Apellido: user.lastname || '',
        Nombre: user.name || '',
        DNI: user.documentNumber || '',
        Servicio: getServiceName(user.service_id),
        'Sesiones por semana': user.sessionsWeek || '',
        Teléfono: user.phone || '',
        Email: user.email || '',
        'Último pago': formatPaymentDate(user.lastPayment),
        Vencimiento: formatPaymentDate(getMembershipExpiration(user)),
        'Cuota al día': paymentAtDay(getMembershipExpiration(user)) ? 'Sí' : 'No',
    }));

    const getExportFileName = (extension) => (
        `socios-${exportFilter === 'unpaid' ? 'cuota-vencida' : 'activos'}-${moment().tz(ARGENTINA_TIMEZONE).format('YYYY-MM-DD')}.${extension}`
    );

    const handleExportExcel = () => {
        const members = getActiveMembersExportData();
        const worksheet = XLSX.utils.json_to_sheet(members);
        worksheet['!cols'] = [
            { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 20 }, { wch: 20 },
            { wch: 18 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 14 },
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Socios activos');
        XLSX.writeFile(workbook, getExportFileName('xlsx'));
    };

    const handleExportPdf = () => {
        const members = getActiveMembersExportData();
        const document = new jsPDF({ orientation: 'landscape' });
        const generatedAt = moment().tz(ARGENTINA_TIMEZONE).format('DD/MM/YYYY HH:mm');
        const reportTitle = exportFilter === 'unpaid'
            ? 'Listado de socios con cuota vencida'
            : 'Listado de socios activos';

        document.setFontSize(16);
        document.text(reportTitle, 14, 15);
        document.setFontSize(10);
        document.text(`Generado: ${generatedAt} | Total: ${members.length}`, 14, 22);
        autoTable(document, {
            startY: 28,
            head: [['Apellido', 'Nombre', 'DNI', 'Servicio', 'Teléfono', 'Email', 'Vencimiento', 'Cuota al día']],
            body: members.map((member) => [
                member.Apellido,
                member.Nombre,
                member.DNI,
                member.Servicio,
                member.Teléfono,
                member.Email,
                member.Vencimiento,
                member['Cuota al día'],
            ]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [33, 37, 41] },
        });
        document.save(getExportFileName('pdf'));
    };

    const handleToggleStatus = async (user) => {
        const isActive = user.active !== 0;
        const nuevoEstado = isActive ? 0 : 1;

        const confirm = await Swal.fire({
            title: `¿${isActive ? 'Deshabilitar' : 'Habilitar'} usuario?`,
            text: `${user.name} ${user.lastname} quedará ${isActive ? 'deshabilitado' : 'habilitado'}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: isActive ? '#d33' : '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: `Sí, ${isActive ? 'deshabilitar' : 'habilitar'}`,
            cancelButtonText: 'Cancelar'
        });

        if (!confirm.isConfirmed) return;

        try {
            const response = await fetch(
                `https://iron.nextriking.com/api/users/${user.id}/toggle-status`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ active: nuevoEstado })
                }
            );
            if (!response.ok) throw new Error('Error al cambiar estado');

            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: nuevoEstado } : u));

            Swal.fire({ icon: 'success', title: isActive ? 'Usuario deshabilitado' : 'Usuario habilitado', timer: 2000, showConfirmButton: false });
        } catch (error) {
            Swal.fire('Error', 'No se pudo cambiar el estado del usuario.', 'error');
        }
    };

    return (
        <div className="d-flex justify-content-center" style={{
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '20px'
        }}>


            <Card className="shadow-lg" style={{ width: '100%', maxWidth: '1400px', padding: '20px', alignSelf: 'flex-start' }}>

                <Card.Body>
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <button type="button" className="btn btn-secondary" onClick={() => navigate("/dashboard")}>
                            Volver atrás
                        </button>
                    </div>
                    <Card.Title className="text-center">
                        Lista de Usuarios <span className="text-muted">
                            ({Array.isArray(users) ? users.filter(user => user.usertype === 3 && user.active !== 0).length : 0})
                        </span>
                    </Card.Title>
                    <p className="text-center text-success" style={{ fontWeight: "bold" }}>
                        ✅ {paidUsersCount}/{totalUsers.length} al día
                    </p>
                    {error && <p className="text-danger">{error}</p>}

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
                        <input
                            type="text"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{ padding: '8px', width: '200px', marginBottom: '10px' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '200px' }}>
                            <label>
                                <input
                                    type="radio"
                                    value="name"
                                    checked={selectedFilter === 'name'}
                                    onChange={handleFilterChange}
                                />
                                Nombre
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    value="lastname"
                                    checked={selectedFilter === 'lastname'}
                                    onChange={handleFilterChange}
                                />
                                Apellido
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    value="documentNumber"
                                    checked={selectedFilter === 'documentNumber'}
                                    onChange={handleFilterChange}
                                />
                                DNI
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                        {['active', 'disabled', 'all'].map((val, i) => (
                            <label key={val} style={{ cursor: 'pointer' }}>
                                <input type="radio" value={val} checked={statusFilter === val}
                                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} />
                                {' '}{['Solo activos', 'Solo deshabilitados', 'Todos'][i]}
                            </label>
                        ))}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        <select
                            className="form-select w-auto"
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="10">10 por página</option>
                            <option value="20">20 por página</option>
                            <option value="50">50 por página</option>
                        </select>
                        <Form.Select
                            className="w-auto"
                            value={exportFilter}
                            onChange={(e) => setExportFilter(e.target.value)}
                            aria-label="Socios a exportar"
                        >
                            <option value="active">Todos los activos</option>
                            <option value="unpaid">Con cuota vencida</option>
                        </Form.Select>
                        <Button variant="success" onClick={handleExportExcel} disabled={!getMembersToExport().length}>
                            Exportar Excel
                        </Button>
                        <Button variant="danger" onClick={handleExportPdf} disabled={!getMembersToExport().length}>
                            Exportar PDF
                        </Button>
                    </div>

                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Acciones</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastname')}>Apellido</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Nombre</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => sortDates('firstPayment')}>Fecha de Primera Cuota</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => sortDates('id')}>Fecha de ultimo pago</th>
                                    <th style={{ cursor: "pointer" }} onClick={payMonthorder}>
                                        Mes Pagado&nbsp;
                                        {ordenMesPagadoAsc ? "⬆️" : "⬇️"}
                                    </th>
                                    <th>Servicio</th>
                                    <th>Sesiones # semana</th>
                                    <th>Nro documento</th>
                                    <th>Fecha de Nacimiento</th>
                                    <th>Teléfono</th>
                                    <th>Email</th>
                                    <th>T & C</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* {filteredUsers.map((user) => ( */}
                                {currentUsers.map((user) => {
                                    const isNearExpiration = isPaymentNearExpiration(user);
                                    const rowStyle = user.active === 0
                                        ? { backgroundColor: '#e9ecef', opacity: 0.7 }
                                        : isNearExpiration
                                            ? { backgroundColor: '#fff0e6' }
                                            : {};

                                    return (
                                    <tr key={user.id} style={rowStyle}>                                        <td>
                                        {editingUserId === user.id ? (
                                            <>
                                                <Button variant="success" className="d-block" onClick={handleSaveEdit}>
                                                    Guardar
                                                </Button>
                                                <Button variant="secondary" className="d-block" style={{ marginTop: '8px' }} onClick={handleCancelEdit}>
                                                    Cancelar
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button variant="warning" className="d-block" onClick={() => handleEdit(user)}>
                                                    Editar
                                                </Button>
                                                <Button variant="danger" className="d-block" style={{ marginTop: '8px' }} onClick={() => handleDelete(user.id)}>
                                                    Eliminar
                                                </Button>
                                                 <Button
                                                     variant={user.active === 0 ? "success" : "secondary"}
                                                     className="d-block"
                                                    style={{ marginTop: '8px' }}
                                                    onClick={() => handleToggleStatus(user)}
                                                >
                                                     {user.active === 0 ? "Habilitar" : "Deshabilitar"}
                                                 </Button>
                                                 <Button
                                                     variant="outline-primary"
                                                     className="d-block"
                                                     style={{ marginTop: '8px' }}
                                                     onClick={() => handleHealthRecord(user)}
                                                 >
                                                     Salud
                                                 </Button>
                                             </>
                                        )}
                                    </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="text"
                                                    name="lastname"
                                                    value={editedValues.lastname || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                user.lastname
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="text"
                                                    name="name"
                                                    value={editedValues.name || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                user.name
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="date"
                                                    name="firstPayment"
                                                    value={editedValues.firstPayment ? new Date(editedValues.firstPayment).toISOString().split('T')[0] : ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                new Date(user.firstPayment).toLocaleDateString()
                                            )}
                                        </td>
                                        <td>
                                            <div
                                                onClick={() => user.lastPayment && handleViewPaymentHistory(user.id, `${user.name} ${user.lastname}`)}
                                                style={{
                                                    cursor: user.lastPayment ? 'pointer' : 'default',
                                                    color: user.lastPayment ? '#4a6eb5' : 'inherit',
                                                    textDecoration: user.lastPayment ? 'underline' : 'none',
                                                    fontWeight: user.lastPayment ? '500' : 'normal'
                                                }}
                                                title={user.lastPayment ? 'Click para ver historial de pagos' : ''}
                                            >
                                                 {user.lastPayment
                                                     ? formatPaymentDate(user.lastPayment)
                                                     : "No registrada"}
                                            </div>

                                            <small className="d-block text-muted">
                                                Vence: {formatPaymentDate(getMembershipExpiration(user))}
                                            </small>

                                            <br />
                                            <Button variant="warning" onClick={() => handleUpdatePayment(user)}>
                                                Registrar pago
                                            </Button>
                                        </td>
                                        <td>
                                            {paymentAtDay(getMembershipExpiration(user)) ? (
                                                <span className="badge bg-success">Sí</span>
                                            ) : (
                                                <span className="badge bg-danger">No</span>
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="text"
                                                    name="service_id"
                                                    value={editedValues.service_id || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                getServiceName(user.service_id)
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="text"
                                                    name="sessionsWeek"
                                                    value={editedValues.sessionsWeek || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                user.sessionsWeek
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="tel"
                                                    name="documentNumber"
                                                    value={editedValues.documentNumber || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                user.documentNumber
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="date"
                                                    name="birthDate"
                                                    value={editedValues.birthDate ? new Date(editedValues.birthDate).toISOString().split('T')[0] : ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                moment.utc(user.birthDate).tz("America/Argentina/Buenos_Aires").format("DD/MM/YYYY")
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="text"
                                                    name="phone"
                                                    value={editedValues.phone || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                user.phone ? (
                                                    <>
                                                        {isNearExpiration && (
                                                            <a
                                                                href={getPaymentReminderLink(user.phone)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Enviar recordatorio de vencimiento"
                                                                className="me-2 text-warning"
                                                            >
                                                                <i className="bi bi-bell-fill"></i>
                                                            </a>
                                                        )}
                                                        <a
                                                            href={`https://wa.me/${user.phone.replace(/\D/g, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Abrir WhatsApp"
                                                        >
                                                            <i className="bi bi-whatsapp"></i>
                                                        </a>
                                                    </>
                                                ) : (
                                                    "Sin número"
                                                )
                                            )}
                                        </td>
                                        <td>
                                            {editingUserId === user.id ? (
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={editedValues.email || ''}
                                                    onChange={handleChange}
                                                />
                                            ) : (
                                                <a href={`mailto:${user.email}`} style={{ color: 'inherit' }}>
                                                    <i className="bi bi-envelope"></i>
                                                </a>
                                            )}
                                        </td>
                                        <td>
                                            {user.tyc === 1 ? (
                                                <span className="badge bg-success">Aceptado</span>
                                            ) : (
                                                <span className="badge bg-danger">No aceptado</span>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>

                        </Table>
                        <nav>
                            <ul className="pagination">
                                {[...Array(totalPages)].map((_, i) => (
                                    <li
                                        key={i}
                                        className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                                    >
                                        <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );

};

export default Members;
